import React, { useState, useEffect } from 'react';
import {
  Button,
  FullscreenDialog,
  HSpacer,
  Modal,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { Box, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DateTime } from 'luxon';
import { ReactComponent as EditSquareIcon } from '@/assets/icons/edit-square.svg';
import { NotificationAddForm } from './NotificationAddForm';
import { PreviewNotification } from './PreviewNotification';
import { ScheduledNotificationApi } from '@/utilities/api/ScheduledNotificationApi';
import { BaseNotificationProps } from '../utils/NotificationTypes';
import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@/constants/QueryKeys';
import { ScheduledNotificationStatus } from '@shared/enums/ScheduledNotificationStatus';
import {
  DeepLinkUrls,
  ScheduledNotificationCommunicationType,
} from '@shared/enums/ScheduledNotificationType';
import {
  handleEditSection,
  isFutureDate,
  getMissingRequiredFields,
} from '../utils/notificationFormUtils';
import { isWithinMinutes } from '@shared/utilities/DateUtils';
import { useTranslation } from 'react-i18next';
import {
  useNotificationCommunicationTypeLabel,
} from '../utils/useNotificationCommunicationTypeLabel';

interface NotificationDetailModalProps extends BaseNotificationProps {
  mode?: 'create' | 'edit',
  onClose: () => void,
  onDraftSaved?: () => void,
  open: boolean,
  retailerName: string,
  title: string,
  communicationType: ScheduledNotificationCommunicationType,
}

export const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  deepLink: initialDeepLink = null,
  message: initialMessageText = '',
  mode: initialMode = 'create',
  notificationId: initialNotificationId,
  onClose,
  onDraftSaved,
  open,
  options: initialOptions = {},
  recipientCount = null,
  csvRecipientData: initialRecipientIds = [],
  retailerId,
  retailerName,
  sendDate: initialScheduleDate,
  status = null,
  testID = 'notification-detail-modal',
  title,
  communicationType,
}) => {

  const { t } = useTranslation();
  const communicationTypeLabel = useNotificationCommunicationTypeLabel();
  const [message, setMessage] = useState(initialMessageText);
  const [sendDate, setSendDate] = useState<Date | undefined>(initialScheduleDate);
  const [options, setOptions] = useState<FilterOptions>(initialOptions);
  const [recipientCountState, setRecipientCountState] = useState<number | null>(recipientCount);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isRescheduleNeeded, setIsRescheduleNeeded] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [showEditForm, setShowEditForm] = useState(initialMode === 'create');
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [currentNotificationId, setCurrentNotificationId] = useState(initialNotificationId);
  const [csvRecipientData, setRecipientIds] = useState(initialRecipientIds);
  const [deepLink, setDeepLink] = useState<DeepLinkUrls | null>(
    initialDeepLink as DeepLinkUrls,
  );
  const [titleState, setTitleState] = useState(title);

  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Track if form has been modified from its initial values
  useEffect(() => {
    const isMessageChanged = message !== initialMessageText;
    const isDeepLinkChanged = deepLink !== initialDeepLink;
    const isOptionsChanged = JSON.stringify(options) !== JSON.stringify(initialOptions);
    const isRecipientCountChanged = recipientCountState !== recipientCount;
    const isRecipientIdsChanged =
      JSON.stringify(csvRecipientData) !== JSON.stringify(initialRecipientIds);
    const isSendDateChanged = !initialScheduleDate
      ? !!sendDate
      : !sendDate
        ? true
        : sendDate.getTime() !== initialScheduleDate.getTime();
    const isTitleChanged = titleState !== title;

    // Check if any field has changed from its initial value
    setIsFormModified(
      isMessageChanged || isDeepLinkChanged || isOptionsChanged ||
      isRecipientCountChanged || isRecipientIdsChanged || isSendDateChanged || isTitleChanged,
    );
  }, [
    message,
    deepLink,
    options,
    recipientCountState,
    csvRecipientData,
    sendDate,
    titleState,
    initialMessageText,
    initialDeepLink,
    initialOptions,
    recipientCount,
    initialRecipientIds,
    initialScheduleDate,
    title,
  ]);

  const handleCloseConfirmation = () => {
    setShowConfirmationDialog(false);
  };

  const handleCloseConfirmationWithReschedule = () => {
    setShowConfirmationDialog(false);
    setIsRescheduleNeeded(false);
    setShowEditForm(true);

    handleEditSection('schedule', testID, undefined, setShowEditForm);
  };

  const saveNotification = async (
    status: ScheduledNotificationStatus,
    successMessage: string,
    closeModalOnSuccess = false,
  ) => {
    try {
      const baseRequestData = {
        retailerId,
        title: titleState,
        communicationType: communicationType as ScheduledNotificationCommunicationType,
        message,
        deepLink: deepLink || null,
        sendDate: sendDate || null,
        status,
        options,
        recipientCount: recipientCountState,
        csvRecipientData,
      };
      if (currentMode === 'edit' && currentNotificationId) {
        await ScheduledNotificationApi.update(currentNotificationId, baseRequestData);
      } else {
        const response = await ScheduledNotificationApi.create(baseRequestData);
        if (response?.id) {
          setCurrentNotificationId(response.id);
          setCurrentMode('edit');
        }
      }
      await queryClient.refetchQueries([QueryKeys.GET_SCHEDULED_NOTIFICATIONS]);
      await queryClient.refetchQueries([QueryKeys.GET_DRAFT_NOTIFICATIONS]);

      openSnackbar(successMessage);

      if (closeModalOnSuccess) {
        onClose();
      }

      return true;
    } catch (error) {
      const errorMsg = t(
        $ => $.notificationBuilder.modal.detail.errors.saveFailed,
        { error: error || status },
      );
      openSnackbar(errorMsg);
      return false;
    }
  };

  const handleSubmit = async () => {
    // validate required fields for scheduling using shared util
    const missingForSchedule = getMissingRequiredFields({
      targetStatus: ScheduledNotificationStatus.Scheduled,
      retailerId,
      title: titleState,
      communicationType,
      message,
      sendDate,
      recipientCountState,
    });
    if (missingForSchedule.length > 0) {
      const errorMsg = t(
        $ => $.notificationBuilder.modal.detail.validation.requiredFields,
        { fields: missingForSchedule.join(', ') },
      );
      openSnackbar(errorMsg, 5000, true);
      return;
    }
    if (sendDate && !isFutureDate(sendDate)) {
      const futureMsg = t(
        $ => $.notificationBuilder.modal.detail.validation.futureDateTime,
      );
      openSnackbar(futureMsg, 5000, true);
      return;
    }

    // Additional validation for edit mode - check 30-minute rule
    if (
      currentMode === 'edit' &&
      status === ScheduledNotificationStatus.Scheduled &&
      isFormModified &&
      sendDate &&
      isWithinMinutes(sendDate, 30)
    ) {
      // Set flag for reschedule needed and show the confirmation dialog
      setIsRescheduleNeeded(true);
      setShowConfirmationDialog(true);
      return;
    }

    await saveNotification(
      ScheduledNotificationStatus.Scheduled,
      t($ => $.notificationBuilder.modal.detail.success.scheduled),
      true,
    );
    setShowConfirmationDialog(false);
  };

  const saveDraft = async () => {
    const missingForDraft = getMissingRequiredFields({
      targetStatus: ScheduledNotificationStatus.Draft,
      retailerId,
      title: titleState,
      communicationType,
    });
    if (missingForDraft.length > 0) {
      const errorMsg = t(
        $ => $.notificationBuilder.modal.detail.validation.requiredFields,
        { fields: missingForDraft.join(', ') },
      );
      openSnackbar(errorMsg, 5000, true);
      return;
    }
    const success = await saveNotification(
      ScheduledNotificationStatus.Draft,
      t($ => $.notificationBuilder.modal.detail.success.draftSaved),
    );
    if (success) {
      onDraftSaved?.();
      onClose();
    }
  };

  return (
    <>
      <FullscreenDialog
        hideTitleBorder
        open={open}
        sx={{
          '& .MuiDialogContent-root': {
            maxWidth: '100% !important',
          },
        }}
        testID={testID}
      >
        <Stack sx={{ padding: '16px 24px' }}>
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Text category="headline-large">
              {currentMode === 'create'
                ? t($ => $.notificationBuilder.modal.detail.createHeadline)
                : (status === ScheduledNotificationStatus.Cancelled ||
                   status === ScheduledNotificationStatus.Sent)
                  ? t($ => $.notificationBuilder.modal.detail.viewHeadline)
                  : t($ => $.notificationBuilder.modal.detail.editHeadline)}
            </Text>
            <Stack alignItems="center" direction="row">
              {(
                !status ||
                status === ScheduledNotificationStatus.Draft
              ) && (
                  <Button
                    color="primary"
                    onClick={saveDraft}
                    startIcon={<EditSquareIcon />}
                    testID={`${testID}-save-draft-button`}
                    variant="text"
                  >
                    {t($ => $.common.actions.saveDraft)}
                  </Button>
              )}
              <HSpacer size="3" />
              <Button
                onClick={onClose}
                testID={`${testID}-cancel-button`}
                variant="text"
              >
                {status === ScheduledNotificationStatus.Scheduled ||
                 status === ScheduledNotificationStatus.Cancelled ||
                 status === ScheduledNotificationStatus.Sent
                  ? t($ => $.common.actions.close)
                  : t($ => $.common.actions.cancel)}
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          sx={{
            padding: '16px 24px 24px',
            position: 'relative',
          }}
        >
          <Box
            data-testid={`${testID}-notification-details-card`}
            sx={{
              padding: '16px 20px 24px 20px',
              width: '246px',
              background: '#00000005',
              height: 'fit-content',
              position: 'absolute',
              left: '24px',
              top: '16px',
              zIndex: 1,
              borderRadius: '8px',
            }}
          >
            <Text
                category="body-small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {t($ => $.notificationBuilder.modal.detail.retailerLabel)}
            </Text>
            <Text category="body-medium">
              {retailerName}
            </Text>
            <VSpacer size="4" />
            <Stack direction="column" spacing={0}>
              <Text
                category="body-small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {t($ => $.notificationBuilder.modal.detail.communicationTypeLabel)}
              </Text>
              <VSpacer size="1" />
              <Text category="body-medium">
                {communicationTypeLabel[communicationType]}
              </Text>
            </Stack>
          </Box>
          {showEditForm ? (
            <NotificationAddForm
              deepLink={deepLink}
              initialOptions={options}
              initialRecipientData={csvRecipientData}
              message={message}
              notificationId={currentNotificationId}
              recipientCount={recipientCountState}
              retailerId={retailerId}
              sendDate={sendDate}
              setDeepLink={setDeepLink}
              setMessage={setMessage}
              setOptions={setOptions}
              setRecipientCount={setRecipientCountState}
              setRecipientIds={setRecipientIds}
              setSendDate={setSendDate}
              setShowConfirmationDialog={setShowConfirmationDialog}
              setTitle={setTitleState}
              status={status}
              testID={testID}
              title={titleState}
            />
          ) : (
            <PreviewNotification
              csvRecipientData={csvRecipientData}
              deepLink={deepLink}
              isEditMode={currentMode === 'edit'}
              message={message}
              onCreateNotification={() => setShowConfirmationDialog(true)}
              onEditSection={(section) =>
                handleEditSection(section, testID, undefined, setShowEditForm)
              }
              options={options}
              recipientCount={recipientCountState}
              retailerId={retailerId}
              sendDate={sendDate}
              status={status}
              testID={`${testID}-preview`}
              title={titleState}
            />
          )}
        </Stack>
      </FullscreenDialog>

      {showConfirmationDialog && (
        <Modal
          containerStyleOverride={{
            padding: '24px',
            borderRadius: '24px',
            maxWidth: '411px',
            textAlign: 'center',
          }}
          onClose={
            isRescheduleNeeded
              ? handleCloseConfirmationWithReschedule
              : handleCloseConfirmation
          }
          open={showConfirmationDialog}
          testID={`${testID}-confirmation-dialog`}
        >
          <Stack alignItems="center">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
            }}>
              <ErrorOutlineIcon
                sx={{
                  height: '80px',
                  width: '80px',
                  color: '#F9A826',
                }}
              />
            </div>
            <VSpacer size="5" />
            <Text category="headline-small">
              {isRescheduleNeeded ? (
                t($ => $.notificationBuilder.modal.detail.reschedule.title)
              ) : (
                t($ => $.notificationBuilder.modal.detail.reschedule.message, {
                  date: !!sendDate && DateTime.fromJSDate(sendDate as Date),
                  time: !!sendDate && DateTime.fromJSDate(sendDate as Date),
                })
              )}
            </Text>
            <VSpacer size="5" />
            <Text category="body-medium">
              {isRescheduleNeeded ? (
                t($ => $.notificationBuilder.modal.detail.reschedule.timeWarning)
              ) : (
                t($ => $.notificationBuilder.modal.detail.reschedule.editInfo)
              )}
            </Text>
            <VSpacer size="7" />
            <Stack direction="row" justifyContent="flex-end" style={{ width: '100%' }}>
              <Button
                onClick={
                  isRescheduleNeeded
                    ? handleCloseConfirmationWithReschedule
                    : handleCloseConfirmation
                }
                testID={`${testID}-go-back-button`}
                variant="text"
              >
                {t($ => $.common.actions.goBack)}
              </Button>
              <HSpacer size="4" />
              <Button
                color="primary"
                onClick={isRescheduleNeeded ? handleCloseConfirmationWithReschedule : handleSubmit}
                testID={`${testID}-confirm-button`}
              >
                {t($ => $.common.actions.confirm)}
              </Button>
            </Stack>
          </Stack>
        </Modal>
      )}
    </>
  );
};
